import { useState } from 'react';
import { useAccounts } from '@/hooks/useAccounts';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface QueueAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queueItem: any;
  onAssign: (queueItemId: string, accountId: string) => Promise<void>;
}

export const QueueAssignmentDialog = ({ open, onOpenChange, queueItem, onAssign }: QueueAssignmentDialogProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const { accounts } = useAccounts();
  const { toast } = useToast();

  const handleAssign = async () => {
    if (!selectedAccountId) {
      toast({
        title: "Selection Required",
        description: "Please select an account to assign this bill to.",
        variant: "destructive",
      });
      return;
    }

    setIsAssigning(true);
    try {
      await onAssign(queueItem.id, selectedAccountId);
      setSelectedAccountId('');
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsAssigning(false);
    }
  };

  if (!queueItem) return null;

  const extractedData = queueItem.extracted_data || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Bill to Account</DialogTitle>
          <DialogDescription>
            Select an account to assign this processed bill to. Review the extracted data below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Extracted Data Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <h4 className="font-medium text-sm">File</h4>
              <p className="text-sm text-muted-foreground">{queueItem.file_name}</p>
            </div>
            <div>
              <h4 className="font-medium text-sm">Customer Name</h4>
              <p className="text-sm text-muted-foreground">
                {extractedData.customer_name || 'Not detected'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm">Account Number</h4>
              <p className="text-sm text-muted-foreground">
                {extractedData.account_number || 'Not detected'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm">Amount</h4>
              <p className="text-sm text-muted-foreground">
                ${extractedData.amount?.toFixed(2) || 'Not detected'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm">Service Type</h4>
              <p className="text-sm text-muted-foreground">
                {extractedData.service_type || 'Not detected'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm">AI Confidence</h4>
              <Badge variant={queueItem.ai_confidence > 0.8 ? 'default' : 'secondary'}>
                {((queueItem.ai_confidence || 0) * 100).toFixed(0)}%
              </Badge>
            </div>
          </div>

          {/* Account Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Account</label>
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an account..." />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{account.account_number}</span>
                      <span className="text-xs text-muted-foreground">
                        {account.service_type} • Status: {account.status}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {accounts.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No accounts available. Create an account first to assign bills.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssign} 
            disabled={!selectedAccountId || isAssigning || accounts.length === 0}
          >
            {isAssigning ? 'Assigning...' : 'Assign Bill'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};